import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useFormContext, useWatch } from '~shared/segments/Form'

import { TPersistedForm, useShouldRememberFormCtx } from '../contexts/FormPersistenceProvider'
import { TOrderForm } from '../contexts/FormProvider'

type TProps = {
  persistForm: (value: TPersistedForm) => void
  resetFormPersistence: () => void
}

const STORE_DEBOUNCE_MS = 1000

/**
 * Handles order form persistence updates
 * on contact info change
 * (only when the customer want the values remembered).
 * Also provides a way to clear that storage.
 */
export const useRememberFormValues = ({ persistForm, resetFormPersistence }: TProps) => {
  const { control } = useFormContext<TOrderForm>()
  const { rememberForm } = useShouldRememberFormCtx()

  // watched form fields
  // ! keep in sync with the stored shape above
  // ! also keep those two arrays bellow in the same order
  const [givenName, familyName, phonePrefix, phoneNumber, email] = useWatch({
    control,
    name: [`givenName`, `familyName`, `phonePrefix`, `phoneNumber`, `email`],
  })

  // persist only after some delay in changes
  const debouncedPersist = useDebouncedCallback(persistForm, STORE_DEBOUNCE_MS)

  // connect the debounced persist function to the value changes
  React.useEffect(() => {
    if (!rememberForm) {
      return
    }

    debouncedPersist({ givenName, familyName, phonePrefix, phoneNumber, email })
  }, [
    email, //
    givenName,
    familyName,
    phoneNumber,
    phonePrefix,
    rememberForm,
    debouncedPersist,
  ])

  // return a function to clear stored values
  return resetFormPersistence
}
